import React, { useState } from 'react'
import styled from 'styled-components'
import { array, shape, string, boolean } from 'prop-types'
import Link from './AnchorOrGatsbyLink.js'
import { Flex, Heading } from 'rebass'
import typography from '../../utils/typography'

import {
  CardHeading,
  VehiclePrice,
  VehiclePreviewBox,
  VehiclePreviewWrapper,
  Callout
} from './components/VehiclePreview'
import Image from '../Image'
import LazyLoad from 'react-lazyload'
import media, { lazyload } from '../../utils/style'
import { ContentBrandFilter } from './components/ContentBrandFilter'
// import { ContentBrandDialog } from './components/ContentBrandDialog'

const FlexWrapper = styled(({ theme, joined, ...rest }) => <Flex {...rest} />)`
  justify-content: center;
  position: relative;
  z-index: 1;
  color: ${(props) => props.theme.colors.white};
`

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px;
  ${media.LargeScreenUp`
    flex-direction: row;
    gap: 40px;
  `}
`

const OfferTitle = styled.div`
  font-size: 20px;
  color: ${(props) => props.theme.colors.green};
  font-weight: 500;
  line-height: 1.2;
  padding: 12px 0 4px;
`

const SaleCategory = styled.div`
  font-size: 16px;
  color: ${(props) => props.theme.colors.grey_light};
  font-weight: 700;
  line-height: 1.2;
`

const ContentBrandModels = ({
  models,
  make,
  calloutImage,
  calloutLink,
  calloutApplyToSubModels,
  enableFilters = false,
  enableDialog = false
}) => {
  const [brandFilter, setBrandFilter] = useState('')
  const [modelFilter, setModelFilter] = useState('')

  const filteredModels = enableFilters
    ? models.filter((model) => {
        if (!!brandFilter && model.brand !== brandFilter) return false
        if (!!modelFilter && model.model !== modelFilter) return false
        return true
      })
    : models

  const modelFilterOptions = [
    ...new Set(
      models
        .filter((model) => !brandFilter || model.brand === brandFilter)
        .map((model) => model.model)
    )
  ].map((model) => {
    return {
      label: model,
      value: model
    }
  })

  const brandFilterOptions = [
    ...new Set(models.map((model) => model.brand))
  ].map((brand) => {
    return {
      label: brand,
      value: brand
    }
  })

  const splitModelsByBrand = filteredModels.reduce((acc, model) => {
    acc[model.brand] = [...(acc[model.brand] || []), model]
    return acc
  }, {})

  const handleBrandFilterChange = (value) => {
    setBrandFilter(value)
    setModelFilter('')
  }

  const brands = Object.keys(splitModelsByBrand)

  return (
    <Flex
      flexDirection="column"
      width={[12 / 12, null, 11 / 12]}
      pt={[20, 40]}
      mb={typography.rhythm(1)}
    >
      {enableFilters && (
        <FilterWrapper>
          <ContentBrandFilter
            label="Brand"
            options={[{ label: 'Any', value: '' }, ...brandFilterOptions]}
            value={brandFilter}
            onSelect={handleBrandFilterChange}
            placeholder="Sort by"
          />
          <ContentBrandFilter
            label="Model"
            options={[{ label: 'Any', value: '' }, ...modelFilterOptions]}
            value={modelFilter}
            onSelect={setModelFilter}
            placeholder="Sort by"
          />
        </FilterWrapper>
      )}

      {brands.length === 0 && (
        <FlexWrapper>
          <Heading as="h2" textAlign="left" py={[20, 40]} px={16}>
            No models found
          </Heading>
        </FlexWrapper>
      )}

      {brands.length > 0 &&
        brands.map((brand) => {
          const models = splitModelsByBrand[brand]
          if (models.length === 0) return null
          return (
            <Flex width="100%" flexDirection="column" key={`cbm:${brand}`}>
              {brand !== 'undefined' && (
                <Heading
                  as="h2"
                  textAlign="center"
                  py={[20, 40]}
                  px={16}
                  fontSize={[6, 7, 8]}
                >
                  {brand}
                </Heading>
              )}
              <FlexWrapper flexWrap="wrap">
                {models &&
                  models.length > 0 &&
                  models.map((model, index) => {
                    // const useDialog = model.type === 'New' && enableDialog
                    // const LinkElement = useDialog ? ContentBrandDialog : Link
                    return (
                      <VehiclePreviewWrapper
                        width={[1 / 1, 1 / 2, 1 / 3, 1 / 4]}
                        key={`cbm:${index}:${model.path}`}
                      >
                        <VehiclePreviewBox
                          px={[20, null, 30]}
                          pt={'4.5vh'}
                          pb={'4.5vh'}
                        >
                          <Link to={model.path}>
                            <LazyLoad
                              offset={lazyload.offset}
                              height={model.isUsed ? 250 : 190}
                              once
                            >
                              <Image
                                resource={model.image}
                                width="400"
                                height={model.isUsed ? 250 : 190}
                                mode="pad"
                                vehicleCategory={model.category}
                                alt={`${make || model.make} ${model.model}`}
                              />
                            </LazyLoad>
                            {calloutApplyToSubModels && calloutImage && (
                              <Callout
                                href={calloutLink}
                                alt="Callout Image Submodel"
                              >
                                <Image
                                  resource={calloutImage}
                                  width="50"
                                  height="50"
                                  mode="pad"
                                />
                              </Callout>
                            )}
                            {model.saleCategory && (
                              <SaleCategory>{model.saleCategory}</SaleCategory>
                            )}
                            <CardHeading as="h4" f={4} mt={[2]}>
                              <span>
                                {make || model.brand} {model.model}
                              </span>
                            </CardHeading>
                            {model.offerTitle && (
                              <OfferTitle>{model.offerTitle}</OfferTitle>
                            )}
                            <VehiclePrice
                              offer={model.offer}
                              f={2}
                              mt={10}
                              mb={15}
                            >
                              {model.price &&
                                (isNaN(model.price)
                                  ? model.price
                                  : `prices start from ${model.price}`)}
                            </VehiclePrice>
                          </Link>
                        </VehiclePreviewBox>
                      </VehiclePreviewWrapper>
                    )
                  })}
              </FlexWrapper>
            </Flex>
          )
        })}
    </Flex>
  )
}

ContentBrandModels.defaultProps = {
  models: [],
  currency: { code: 'GBP', symbol: '£' }
}

ContentBrandModels.propTypes = {
  models: array,
  currency: shape({ code: string, symbol: string }),
  make: string,
  calloutImage: string,
  calloutLink: string,
  calloutApplyToSubModels: boolean,
  title: string,
  enableFilters: boolean,
  enableDialog: boolean
}

export default ContentBrandModels
